import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { MessageCircle, Plus, Trash2, Calendar, Users, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import API from '@/config/api';

const COMM_TYPES = [
  { value: 'in_person', label: 'In-Person Conference' },
  { value: 'phone', label: 'Phone Call' },
  { value: 'video', label: 'Video Call' },
  { value: 'written', label: 'Written Report' },
  { value: 'email', label: 'Email' },
];

const SUGGESTED_TOPICS = [
  'Social/Emotional', 'Cognitive', 'Physical', 'Language',
  'Self-Help', 'Behavior', 'Toileting', 'Sleep', 'Nutrition', 'Milestones',
];

/**
 * Parent-Staff development communications for one child.
 * DCF 251.07(5): every 3 months for under 2, every 6 months for ages 2–5.
 */
const ChildDevelopmentCommunications = ({ childId, childName, ageYears, readOnly = false }) => {
  const { getAuthHeaders } = useAuth();
  const [comms, setComms] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    communication_date: new Date().toISOString().split('T')[0],
    communication_type: 'in_person',
    staff_present: '',
    parents_present: '',
    topics: [],
    milestones_discussed: '',
    concerns: '',
    action_items: '',
    notes: '',
  });

  const cadenceMonths = ageYears < 2 ? 3 : 6;
  const cadenceLabel = cadenceMonths === 3 ? 'Every 3 months (under 2)' : 'Every 6 months (ages 2+)';
  const notRequired = false;

  useEffect(() => { if (childId) fetchComms(); /* eslint-disable-line */ }, [childId]);

  const fetchComms = async () => {
    try {
      const res = await axios.get(`${API}/children/${childId}/development-communications`, getAuthHeaders());
      setComms(res.data.communications || []);
    } catch {
      toast.error('Failed to load communications');
    }
  };

  const lastComm = comms[0];
  let nextDueText = '—';
  let nextUrgency = 'ok';
  if (!notRequired && cadenceMonths) {
    const baseline = lastComm?.communication_date ? new Date(lastComm.communication_date + 'T12:00:00') : null;
    if (baseline) {
      const next = new Date(baseline);
      next.setMonth(next.getMonth() + cadenceMonths);
      const days = Math.round((next - new Date()) / 86400000);
      nextDueText = next.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
      nextUrgency = days < 0 ? 'overdue' : days <= 14 ? 'critical' : days <= 30 ? 'warning' : 'ok';
    } else {
      nextDueText = 'Log first communication to start tracking';
      nextUrgency = 'warning';
    }
  }

  const save = async () => {
    if (!form.communication_date) { toast.error('Date required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/children/${childId}/development-communications`, form, getAuthHeaders());
      toast.success('Communication logged');
      setShowAdd(false);
      setForm({
        communication_date: new Date().toISOString().split('T')[0],
        communication_type: 'in_person',
        staff_present: '',
        parents_present: '',
        topics: [],
        milestones_discussed: '',
        concerns: '',
        action_items: '',
        notes: '',
      });
      fetchComms();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this communication record?')) return;
    try {
      await axios.delete(`${API}/development-communications/${id}`, getAuthHeaders());
      toast.success('Communication deleted');
      fetchComms();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const toggleTopic = (t) => {
    setForm(f => ({
      ...f,
      topics: f.topics.includes(t) ? f.topics.filter(x => x !== t) : [...f.topics, t],
    }));
  };

  const urgencyClass = {
    ok: 'bg-green-100 text-green-700',
    warning: 'bg-amber-100 text-amber-700',
    critical: 'bg-orange-100 text-orange-700',
    overdue: 'bg-red-100 text-red-700',
  }[nextUrgency];

  return (
    <Card className="border-l-4 border-l-purple-500" data-testid="child-dev-communications">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <CardTitle className="text-base">Parent-Staff Development Communications</CardTitle>
              <p className="text-xs text-muted-foreground">DCF 251.07(5) — {cadenceLabel}{notRequired ? ' (child is 5+, no set cadence required)' : ''}</p>
            </div>
          </div>
          {!readOnly && !notRequired && (
            <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-dev-comm-btn">
              <Plus className="w-3 h-3 mr-1" />
              Log
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!notRequired && (
          <div className="flex items-center gap-3 p-2 bg-muted/40 rounded-lg mb-3">
            <Calendar className="w-4 h-4 text-purple-600" />
            <div className="flex-1 text-sm">
              <span className="font-medium">Next due: {nextDueText}</span>
              <Badge className={cn('ml-2 text-[10px]', urgencyClass)}>
                {nextUrgency === 'ok' ? <CheckCircle className="w-3 h-3 mr-1 inline" /> : <AlertCircle className="w-3 h-3 mr-1 inline" />}
                {nextUrgency === 'ok' ? 'On Track' : nextUrgency.charAt(0).toUpperCase() + nextUrgency.slice(1)}
              </Badge>
            </div>
          </div>
        )}

        {comms.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">No communications logged yet.</p>
        ) : (
          <div className="space-y-2">
            {comms.map(c => (
              <div key={c.id} className="p-3 bg-muted/30 rounded-lg" data-testid={`dev-comm-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-sm">
                        {new Date(c.communication_date + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                      <Badge variant="outline" className="text-[10px]">
                        {COMM_TYPES.find(t => t.value === c.communication_type)?.label || c.communication_type}
                      </Badge>
                      {c.topics?.length > 0 && c.topics.map(t => (
                        <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>
                      ))}
                    </div>
                    {c.parents_present && (
                      <p className="text-xs text-muted-foreground mt-1">
                        <Users className="w-3 h-3 mr-1 inline" />
                        Parents: {c.parents_present}{c.staff_present ? ` • Staff: ${c.staff_present}` : ''}
                      </p>
                    )}
                    {c.milestones_discussed && <p className="text-xs mt-1"><strong>Milestones:</strong> {c.milestones_discussed}</p>}
                    {c.concerns && <p className="text-xs mt-1 text-amber-700"><strong>Concerns:</strong> {c.concerns}</p>}
                    {c.action_items && <p className="text-xs mt-1"><strong>Action Items:</strong> {c.action_items}</p>}
                    {c.notes && <p className="text-xs mt-1 text-muted-foreground">{c.notes}</p>}
                  </div>
                  {!readOnly && (
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => remove(c.id)} data-testid={`delete-dev-comm-${c.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-xl" data-testid="log-dev-comm-dialog">
          <DialogHeader>
            <DialogTitle>Log Development Communication</DialogTitle>
            <DialogDescription>{childName} — {cadenceLabel}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={form.communication_date} onChange={(e) => setForm({ ...form, communication_date: e.target.value })} data-testid="dev-comm-date-input" />
              </div>
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={form.communication_type} onValueChange={(v) => setForm({ ...form, communication_type: v })}>
                  <SelectTrigger data-testid="dev-comm-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COMM_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Staff Present</Label>
                <Input value={form.staff_present} onChange={(e) => setForm({ ...form, staff_present: e.target.value })} placeholder="e.g. Jane Smith (Lead Teacher)" />
              </div>
              <div>
                <Label className="text-xs">Parents Present</Label>
                <Input value={form.parents_present} onChange={(e) => setForm({ ...form, parents_present: e.target.value })} placeholder="e.g. Mom, Dad" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Topics Discussed</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {SUGGESTED_TOPICS.map(t => (
                  <Badge
                    key={t}
                    variant={form.topics.includes(t) ? 'default' : 'outline'}
                    className="cursor-pointer text-[10px]"
                    onClick={() => toggleTopic(t)}
                    data-testid={`topic-${t.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {t}
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Milestones Discussed</Label>
              <Textarea rows={2} value={form.milestones_discussed} onChange={(e) => setForm({ ...form, milestones_discussed: e.target.value })} placeholder="e.g. Started using 2-word phrases, walking independently" />
            </div>
            <div>
              <Label className="text-xs">Concerns</Label>
              <Textarea rows={2} value={form.concerns} onChange={(e) => setForm({ ...form, concerns: e.target.value })} placeholder="Any concerns raised by parent or staff" />
            </div>
            <div>
              <Label className="text-xs">Action Items / Follow-Up</Label>
              <Textarea rows={2} value={form.action_items} onChange={(e) => setForm({ ...form, action_items: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Additional Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="save-dev-comm-btn">
                {saving ? 'Saving…' : 'Save Communication'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ChildDevelopmentCommunications;
